import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import {MatDialog} from "@angular/material/dialog";
import {Observable, Subscription} from "rxjs";
import {take} from "rxjs/operators";
import {MatSelectChange} from "@angular/material/select";
import {ImageGalleryComponent} from "../image-gallery.component";
import {toolbarOptions} from "../../../shared/model/toolbar-constants";
import {ViewerConstants} from "../../../shared/model/viewer-constants";
import {IMetadataImage} from "../../../viewer-components/interfaces/i-metadataimage";
import {ToolbarService} from "../../../viewer-components/services/internal-services/toolbar.service";
import {
    ImageGalleryService
} from "../../../viewer-components/services/internal-services/image-gallery/image-gallery.service";
import {LoadingService} from "../../../viewer-components/services/internal-services/loading.service";
import {LocalStorageService} from "../../../viewer-components/services/internal-services/local-storage.service";
import {LayerService} from "../../../viewer-components/services/internal-services/layer.service";
import {MapService} from "../../../viewer-components/services/internal-services/map.service";
import {FeatureFlagsService} from "../../../viewer-components/services/internal-services/feature-flags.service";
import {GeoService} from "../../../viewer-components/services/internal-services/geo.service";
import {IGalleryStatus} from "../../../viewer-components/interfaces/i-gallery-status";
import {ImagesService} from "../../../viewer-components/services/internal-services/images.service";
import {IGalleryCollection} from "../../../viewer-components/interfaces/i-gallery-collection";
import {MapStatus} from "../../../viewer-components/model/map-status";
import {
    GoogleAnalyticsConstantsCategory,
    GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";
import {DialogGalleryComponent} from "../dialog-gallery/dialog-gallery.component";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics.service";
import {
    VIEWER_SERVICE
} from "../../../viewer-components/services/internal-services/viewer-services/inject-base-viewer.service";
import {
    BaseViewerService
} from "../../../viewer-components/services/internal-services/viewer-services/base-viewer.service";

@Component({
    selector: "app-dock-gallery",
    templateUrl: "./dock-gallery.component.html",
    styleUrls: ["./dock-gallery.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class DockGalleryComponent extends ImageGalleryComponent implements AfterViewInit, AfterContentChecked {
    isCollapseButtonShown = false;

    isCollapsed = false;

    showLeftScroll = false;

    showRightScroll = false;

    private _enablePanel = false;

    displayPanel = false;

    toolbarOption = toolbarOptions.ADDITIONAL_IMAGES;

    filterMutationObserver: MutationObserver;

    initializedView = false;

    obliqueAvailable: boolean;

    displayTooltip = false;

    displayGallery = false;

    initDataSubscription: Subscription;

    mode = ViewerConstants.IMAGE_GALLERY_MODES.DOCK;

    @ViewChild("galleryPanel")
    galleryPanel: ElementRef;

    @ViewChild("filterContainer") filterContainer: ElementRef;

    @Input() set enabledTool(tool: string) {
        if (this.toolbarOption !== tool) {
            this.hideGalleryPanel();
        } else {
            this.initialize();
        }
    }

    @Input() set display(display: boolean) {
        this.displayGallery = display;
    }

    @Input() set zoomEvent(zoomEvent: any) {
        if (zoomEvent && zoomEvent.map.getZoom()) {
            this.enabledPanel = zoomEvent.map.getZoom() >= ViewerConstants.IMAGE_GALLERY_ZOOM_LIMIT;
        }
    }

    @Input() set loadImages(images: IMetadataImage[]) {
        if (this.displayPanel) {
            this.handleLocationUpdate(this.galleryService.getGalleryStatus(), this.mapService.getMapStatus());
        }
    }

    constructor(
        @Inject(VIEWER_SERVICE) viewerService: BaseViewerService,
        private matDialog: MatDialog,
        private toolbarService: ToolbarService,
        googleAnalyticsService: GoogleAnalyticsService,
        galleryService: ImageGalleryService,
        private loadingService: LoadingService,
        private storageService: LocalStorageService,
        layerService: LayerService,
        mapService: MapService,
        cdr: ChangeDetectorRef,
        flagService: FeatureFlagsService,
    ) {
        super(viewerService, googleAnalyticsService, galleryService, layerService, mapService, cdr, flagService);
    }

    // region LifeCycle
    ngAfterViewInit() {
        // TODO: REMOVE THIS AND HANDLE FROM VIEWER COMPONENT
        this.loadingService.showGallery$.pipe(take(1)).subscribe((result) => {
            if (result === "gallery") {
                this.handleReopenGallery();
            }
        });
    }

    /**
     * Check when the view comes visible and update the UX
     */
    ngAfterContentChecked() {
        if (!this.initializedView && this.filterContainer && this.filterContainer.nativeElement.offsetParent != null) {
            this.initializedView = true;
            this.createFilterMutationObserver();
            this.updateViewStatus();
        }
    }

    createFilterMutationObserver(): void {
        if (!this.filterContainer || this.filterMutationObserver) {
            return;
        }
        // observe changes and update the arrow visibility
        this.filterMutationObserver = new MutationObserver((mutations) => {
            mutations.forEach(() => {
                this.handleArrowsVisibility();
            });
        });

        // add observer to material group button value
        this.filterMutationObserver.observe(this.filterContainer.nativeElement, {
            attributes: true,
            subtree: true,
            attributeFilter: ["ng-reflect-value"],
        });
        // add observer to display panel hidden attribute
        this.filterMutationObserver.observe(this.galleryPanel.nativeElement, {
            attributes: true,
            attributeFilter: ["class"],
        });
    }

    // endregion

    // region Load Data

    /**
     * Initialize gallery
     * Get data for the gallery and update toolbar option
     */
    initialize(): void {
        this.toolbarService.enableTool(this.toolbarOption);
        this.initDataSubscription = this.getInitData().subscribe((status) => {
            const center = GeoService.toWgs84(this.mapService.getMapStatus().center);
            GeoService.reverseGeocode([center.geometry.coordinates[1], center.geometry.coordinates[0]]).subscribe(
                (geocoderResult) => {
                    status.address = geocoderResult[0].formatted_address;
                    this.openGallery(status);
                },
            );
        });
    }

    /**
     * Get data from the gallery service
     */
    getInitData(): Observable<IGalleryStatus> {
        let images = this.viewerService.getLoadedImages();
        images = this.filterVisibleImages(images);
        this.galleryService.updateFilteredImages(images);
        const center = GeoService.toWgs84(this.mapService.getMapStatus().center.geometry).coordinates;
        const extent = GeoService.toWgs84(this.mapService.getMapStatus().extent.geometry);
        return this.galleryService.calculateInitStatus(images, extent, center, null);
    }

    /**
     * Phoenix viewer the images are not filtered, always oblique mode available
     * @param images
     */
    filterVisibleImages(images: IMetadataImage[]): IMetadataImage[] {
        const obliqueAvailable = images.length > 0;
        this.galleryService.setObliqueAvailable(obliqueAvailable);
        this.obliqueAvailable = obliqueAvailable;
        images = images.filter((image) => !image.archived);
        const enabledAOIs = this.viewerService.getProjectStatus()?.enabledAOIs || [];
        return ImagesService.filterImagesInBBox(images, this.viewerService.getCoreMap().getExtent(), 0.85, enabledAOIs);
    }

    /**
     * Filter oblique images
     * @param images
     */

    processGalleryStatus(status: IGalleryStatus): Observable<IGalleryCollection> {
        this.galleryService.setGalleryStatus(status);
        const selectedCollection = this.galleryService.getInitCollection(status.collections);

        return this.galleryService.selectCollection(selectedCollection);
    }

    /**
     * Check if the dock should be opened
     * Check if we are on the same location than when we close before
     */
    handleReopenGallery() {
        const savedStatus = this.galleryService.recoverGalleryStatus();
        const isDockOpen = this.galleryService.isDockOpen();
        if (!this.displayPanel && isDockOpen && savedStatus) {
            const sameLocation = GeoService.isCenterEquals(
                GeoService.toWgs84(savedStatus.center).coordinates,
                this.viewerService.getLocation(),
            );
            if (sameLocation) {
                this.togglePanelDisplay();
            }
        }
    }

    /**
     * Check if the view extent no longer intersect with initial extent for the gallery
     * If the map extent not intersect we update the gallery images
     * @param galleryStatus status for the gallery
     * @param mapStatus current map status
     */
    handleLocationUpdate(galleryStatus: IGalleryStatus, mapStatus: MapStatus) {
        if (!galleryStatus || !mapStatus) {
            return;
        }
        const outside = !GeoService.polygonsIntersection(
            galleryStatus.extent,
            GeoService.toWgs84(mapStatus.extent.geometry),
        );
        if (outside) {
            this.initialize();
        }
    }

    /**
     * Process gallery status and show the gallery
     * @param status
     */
    openGallery(status: IGalleryStatus): void {
        this.processGalleryStatus(status).subscribe(() => {
            this.showGalleryPanel();
        });
    }

    /**
     * Show gallery panel and save data on local storage
     */
    showGalleryPanel() {
        this.displayPanel = true;
        this.galleryService.setDockOpen(true);

        setTimeout(() => {
            this.selectImage = this.selectedImage;
            this.cdr.detectChanges();
        }, 500);
    }

    hideGalleryPanel() {
        this.initializeScrolls();
        this.closeGallery();
    }

    // endregion

    // region UI events
    togglePanelDisplay() {
        if (this.displayPanel) {
            this.toolbarService.enableTool(toolbarOptions.NONE);
            this.isCollapseButtonShown = false;
            this.isCollapsed = false;
            this.hideGalleryPanel();
        } else {
            this.initialize();
        }

        this.googleAnalyticsService.setGoogleAnalyticsEvent(
            GoogleAnalyticsConstantsEvents.OPEN_IMAGE_GALLERY,
            GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
            this.displayPanel,
        );
    }

    /**
     * Change collection by year
     * @param event
     */
    selectCollection(event: MatSelectChange) {
        const newCollection = this.collections.find((collection) => collection.year === event.value);
        this.galleryService.selectCollection(newCollection);
        this.handleArrowsVisibility();

        this.cdr.detectChanges();

        this.googleAnalyticsService.setGoogleAnalyticsEvent(
            GoogleAnalyticsConstantsEvents.IMAGE_GALLERY_YEAR_FILTER,
            GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
            true,
        );
    }

    openDialogGallery(): void {
        const galleryStatus = this.galleryService.getGalleryStatus();
        this.galleryService.setDisplayGallery(true);

        const dialogConfig = {
            width: "80%",
            height: "75%",
            panelClass: "gallery-dialog",
            disableClose: true,
            data: {
                status: galleryStatus,
                viewerService: this.viewerService,
                galleryService: this.galleryService,
                googleAnalyticsService: this.googleAnalyticsService,
            },
        };
        const dialogRef = this.matDialog.open(DialogGalleryComponent, dialogConfig);

        dialogRef.afterOpened().subscribe(() => {
            this.toggleGalleryCollapsingState(true);
        });
        dialogRef.afterClosed().subscribe(() => {
            this.toggleGalleryCollapsingState(false);
        });
    }

    closeGallery() {
        this.displayPanel = false;
        this.cdr.detectChanges();
        if (!this.isCollapsed) {
            this.galleryService.removeGalleryData();
            if (this.initDataSubscription) this.initDataSubscription.unsubscribe();
        }
    }

    viewOnMap(event: any, image: IMetadataImage): void {
        if (event.target.innerHTML !== ViewerConstants.CONTENT_COPY) {
            this.galleryService.viewImageOnMap(image);
            this.googleAnalyticsService.setGoogleAnalyticsEvent(
                GoogleAnalyticsConstantsEvents.CLICK_IMAGE_IN_GALLERY,
                GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
                true,
            );
        }
    }

    toggleGalleryCollapsingState(enableCollapse: boolean): void {
        this.isCollapsed = enableCollapse;

        if (enableCollapse) {
            // this.toolbarService.enableTool(toolbarOptions.ADDITIONAL_IMAGES);
            this.displayPanel = true;
        } else {
            // this.toolbarService.enableTool(this.toolbarOption);
            this.showGalleryPanel();
        }
        this.cdr.detectChanges()
    }

    set enabledPanel(enabled: boolean) {
        this._enablePanel = enabled;
        if (this.displayPanel && !enabled) {
            this.toolbarService.enableTool(toolbarOptions.NONE);
        }
    }

    get enabledPanel(): boolean {
        return this._enablePanel;
    }

    // endregion

    switchBoolValue(flag: string, value: boolean): void {
        this[flag] = value;
    }

    // region UX Events

    getToolTip(): string {
        if (!this.enabledPanel) {
            return "Zoom in to open image gallery";
        }
        if (!this.displayPanel) {
            return "Image Gallery";
        }
        return "";
    }

    /**
     * Update view status after view comes visible
     */
    updateViewStatus() {
        this.handleArrowsVisibility();
        this.updateScrollPosition();
    }

    /**
     * Set scroll to start for gallery and filter container
     */
    initializeScrolls() {
        if (!this.filterContainer || !this.galleryContainer) {
            return;
        }
        this.filterContainer.nativeElement.scrollLeft = 0;
        this.galleryContainer.nativeElement.scrollTop = 0;
    }

    /**
     * Update scroll position if have some location stored
     */
    updateScrollPosition() {
        if (this.storageService.get("filter-scroll")) {
            this.filterContainer.nativeElement.scrollLeft = this.storageService.get("filter-scroll");
        }
    }

    slideScroll(direction: string): void {
        if (!this.filterContainer) {
            return;
        }
        const STEP = 5;
        const DURATION = 25;
        const filters = Array.from(this.filterContainer.nativeElement.querySelectorAll(".mat-button-toggle"));
        let totalLength = 0;
        filters.forEach((element: HTMLElement) => {
            totalLength += element.offsetWidth;
        });
        const distance = Math.round(totalLength / filters.length) * 2;
        let scrollAmount = 0;

        const scrollAction = setInterval(() => {
            if (scrollAmount >= distance) {
                clearInterval(scrollAction);
            }
            if (direction === "left") {
                this.filterContainer.nativeElement.scrollLeft -= STEP;
            } else {
                this.filterContainer.nativeElement.scrollLeft += STEP;
            }
            scrollAmount += STEP;
            this.storageService.set("filter-scroll", this.filterContainer.nativeElement.scrollLeft);
        }, DURATION);
    }

    onScroll() {
        this.handleArrowsVisibility();
    }

    /**
     * Calculate scroll size to show arrows if its necessary
     */
    handleArrowsVisibility() {
        if (!this.filterContainer) {
            return;
        }

        const {scrollWidth, scrollLeft, offsetWidth} = this.filterContainer.nativeElement;
        const showArrows = scrollWidth > offsetWidth;
        this.showLeftScroll = showArrows && scrollLeft > 0;
        this.showRightScroll = showArrows && scrollLeft < scrollWidth - offsetWidth - 30;
    }

    isOrientationVisible(orientation: string): boolean {
        this.handleArrowsVisibility();
        return super.isOrientationVisible(orientation);
    }

    // endregion
}
